import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth, api } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Save, Loader2, Check, BadgeCheck, Lock, GraduationCap, Mail } from 'lucide-react';
import { toast } from 'sonner';

const StudentProfilePage = () => {
  const { user, updateUser } = useAuth();
  const { theme } = useTheme();
  const isDark = theme?.isDark;

  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Assigned teachers state
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [className, setClassName] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    fetchAssignedTeachers();
  }, []);

  const fetchAssignedTeachers = async () => {
    try {
      const response = await api.get('/student/assigned-teachers');
      setAssignedTeachers(response.data.teachers || []);
      setClassName(response.data.class_name);
    } catch (error) {
      console.error('Failed to fetch assigned teachers:', error);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/auth/profile', { name: name.trim() });
      
      // Update local user state
      if (updateUser) {
        updateUser(response.data);
      }
      
      setSaved(true);
      toast.success('Profile updated successfully!');
      
      // Reset saved indicator after 3 seconds
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setChangingPassword(true);
    try {
      await api.put('/auth/profile', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      setPasswordChanged(true);
      toast.success('Password changed successfully!');
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Reset success indicator after 3 seconds
      setTimeout(() => setPasswordChanged(false), 3000);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail === 'Current password is incorrect') {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            My Profile
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            Update your profile information
          </p>
        </div>

        {/* Assigned Teacher Card - NEW */}
        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <GraduationCap className="h-5 w-5 text-emerald-500" />
              Assigned Teacher{assignedTeachers.length > 1 ? 's' : ''}
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : ''}>
              {className ? `Teachers assigned to Class ${className}` : 'Your class teachers'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingTeachers ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
              </div>
            ) : assignedTeachers.length === 0 ? (
              <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <GraduationCap className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No teacher assigned to your class yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignedTeachers.map((teacher, index) => (
                  <div 
                    key={teacher.id || index}
                    className={`p-4 rounded-xl ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-50 hover:bg-slate-100'} transition-colors`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-full ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                        <GraduationCap className={`h-6 w-6 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {teacher.name}
                        </h3>
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <Mail className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                            <a 
                              href={`mailto:${teacher.email}`}
                              className={`text-sm ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-indigo-600 hover:text-indigo-700'}`}
                            >
                              {teacher.email}
                            </a>
                          </div>
                          {teacher.teacher_message && (
                            <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                              <p className={`text-sm italic ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                "{teacher.teacher_message}"
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Student Info Card */}
        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <BadgeCheck className="h-5 w-5 text-cyan-500" />
              Student Information
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : ''}>
              Your student ID and class details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Student ID
                </p>
                <p className={`text-xl font-bold font-mono ${isDark ? 'text-cyan-400' : 'text-indigo-600'}`}>
                  {user?.student_id || '-'}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Roll No
                </p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {user?.roll_no || '-'}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Email
                </p>
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {user?.email || '-'}
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <p className={`text-xs uppercase tracking-wider mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Role
                </p>
                <p className={`text-sm font-medium capitalize ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>
                  {user?.role || '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Card */}
        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <User className="h-5 w-5 text-violet-500" />
              Edit Profile
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : ''}>
              Update your display name
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className={isDark ? 'text-white' : ''}>
                Your Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className={isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-slate-500' : ''}
                data-testid="profile-name-input"
              />
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                This name will be displayed on the leaderboard and reports
              </p>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className={`w-full sm:w-auto bg-gradient-to-r ${theme?.colors?.primary || 'from-cyan-500 to-violet-500'} text-white`}
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Lock className="h-5 w-5 text-amber-500" />
              Change Password
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : ''}>
              Update your login password
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password" className={isDark ? 'text-white' : ''}>
                Current Password
              </Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                className={isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-slate-500' : ''}
                data-testid="current-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password" className={isDark ? 'text-white' : ''}>
                New Password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className={isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-slate-500' : ''}
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className={isDark ? 'text-white' : ''}>
                Confirm New Password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className={isDark ? 'bg-white/10 border-white/20 text-white placeholder:text-slate-500' : ''}
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className={`w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 text-white`}
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : passwordChanged ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Changed!
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Change Password
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-cyan-50 border border-cyan-200'}`}>
          <p className={`text-sm ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
            💡 <strong>Tip:</strong> Your Student ID ({user?.student_id}) is your login username. 
            If you forget your password, ask your teacher to reset it for your class.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentProfilePage;
